import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import services from "@/services";
import type { User } from "@/models";
import { useToaster } from "ram-components";

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const toaster = useToaster();

  const user = ref<User | null>(null);
  const loading = ref(false);
  
  const isLoggingOut = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const userRole = computed(() => user.value?.userProfile || "");
  const currentUserID = computed(() => user.value?.recordID || 0);


  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

  function clearStorage() {
    localStorage.removeItem("activeUserID");
    localStorage.removeItem("activeUserEmail");
    localStorage.removeItem("activeUserProfile");
  }

  function resetInactivityTimer() {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    
    
    if (isAuthenticated.value) {
      inactivityTimer = setTimeout(() => {
        handleInactivityLogout();
      }, import.meta.env.RAM_INACTIVITY_LIMIT);
    }
  }

 
  async function handleInactivityLogout() {
    toaster.push({
      type: "error",
      text: "Logging out due to inactivity.",
    });
    await logout(true); 
  }

  async function login(emailID: string, password: string) {
    loading.value = true;
    try {
      const captchaToken = "";
      const response = await services.login(emailID, password, captchaToken);
      const loginData = response.data;

      if (loginData.result && loginData.result.result === 1) {
        const { userID, emailID: loginEmail, userProfile: loginRole } = loginData.result;

        localStorage.setItem("activeUserID", userID.toString());
        localStorage.setItem("activeUserEmail", loginEmail);
        localStorage.setItem("activeUserProfile", loginRole);

        await fetchUser();
        resetInactivityTimer();
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function fetchUser() {
    if (isLoggingOut.value) return;

    const storedId = Number(localStorage.getItem("activeUserID"));
    const storedEmail = localStorage.getItem("activeUserEmail");

    if (!storedId) return;

    try {
      const response = await services.getUserById(storedId);
      const apiResponse = response.data;

      if (apiResponse.statusCode === 200 && apiResponse.result) {
        const fetchedUser = apiResponse.result;

        const isEmailMatch = fetchedUser.emailAddress.toLowerCase() === storedEmail?.toLowerCase();
        const isIdMatch = fetchedUser.recordID === storedId;

        if (isIdMatch && isEmailMatch) {
           user.value = fetchedUser;
        } else {
           await logout(false); 
        }
      } else {
        await logout(false);
      }
    } catch (error) {
      await logout(false);
    }
  }

async function logout(notifyBackend = true) {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  if (isLoggingOut.value) return;
  
  isLoggingOut.value = true;

  const userID = user.value?.recordID || Number(localStorage.getItem("activeUserID"));

  user.value = null; 
  clearStorage();

  router.push("/login");

  if (userID && notifyBackend) {
    services.logout(userID).catch(error => {
      console.warn("Backend notified of logout failure, but user is already cleared locally.");
    });
  }

  isLoggingOut.value = false; 
}

  return {
    user,
    loading,
    isAuthenticated,
    userRole,
    currentUserID,
    login,
    logout,
    fetchUser,
    resetInactivityTimer,
  };
});